import { TrendingUp } from "lucide-react";
import React from "react";
import {
  getSignedWeight,
  type InvestorStrategyRecord,
} from "../../services/investorStrategyService";
import { formatPercentage } from "../../utils/numberUtils";

interface UserInvestorStrategyCardProps {
  appearance?: "default" | "alpha";
  isDarkMode: boolean;
  userId: string;
  sidebarStrategies: InvestorStrategyRecord[];
  sidebarStrategiesLoading: boolean;
  sidebarStrategiesError: boolean;
  sidebarManagedCount: number;
}

const UserInvestorStrategyCard: React.FC<UserInvestorStrategyCardProps> = ({
  appearance = "default",
  isDarkMode,
  userId,
  sidebarStrategies,
  sidebarStrategiesLoading,
  sidebarStrategiesError,
  sidebarManagedCount,
}) => {
  return (
    /* Investor Strategy Widget */
    <div className={appearance === "alpha"? `rounded-v7-panel border border-v7-ink/15 bg-v7-white` : `rounded-lg border ${isDarkMode ?"border-gray-700 bg-gray-800/30" : "border-gray-200 bg-white"}`}>
      <div className={appearance === "alpha" ? "flex items-center justify-between p-3 border-b border-v7-ink/15" : "flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700"}>
        <div className="flex items-center gap-2">
          <TrendingUp className={appearance === "alpha"? `w-4 h-4 text-v7-ink/60` : `w-4 h-4 ${isDarkMode ?"text-gray-400" : "text-gray-500"}`} />
          <h4 className={appearance === "alpha"? `text-sm font-medium text-v7-ink-soft` : `text-sm font-medium ${isDarkMode ?"text-gray-200" : "text-gray-700"}`}>
            Investor Strategy
          </h4>
        </div>
        <a
          href={`/admin/users/${userId}/strategy`}
          className={appearance === "alpha" ? `text-xs px-2 py-1 rounded-v7-control text-v7-bronze-dark hover:bg-v7-paper` : `text-xs px-2 py-1 rounded ${
            isDarkMode
              ? "text-blue-400 hover:bg-blue-900/30"
              : "text-blue-600 hover:bg-blue-50"
          }`}
        >
          View
        </a>
      </div>
      <div className="p-3">
        {sidebarStrategiesLoading ? (
          <div className="flex items-center gap-2">
            <div className={appearance === "alpha" ? "animate-spin rounded-full h-4 w-4 border-b-2 border-v7-ink/25" : "animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"}></div>
            <span className={appearance === "alpha"? `text-sm text-v7-ink/60` : `text-sm ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>Loading...</span>
          </div>
        ) : sidebarStrategiesError ? (
          <div className={appearance === "alpha" ? "text-sm text-v7-rust" : "text-sm text-red-500"}>
            Failed to load strategy
          </div>
        ) : sidebarStrategies.length > 0 ? (
          <div className="space-y-2">
            <div className={appearance === "alpha"? `text-sm text-v7-ink-soft` : `text-sm ${isDarkMode ?"text-gray-200" : "text-gray-700"}`}>
              {sidebarStrategies.length} product{sidebarStrategies.length !== 1 ? "s" : ""},{" "}
              {formatPercentage(
                sidebarStrategies.reduce((sum, s) => sum + getSignedWeight(s), 0)
              )} allocated
            </div>
            {sidebarManagedCount > 0 && (
              <span className={appearance === "alpha" ? "inline-flex rounded-full border border-v7-bronze/30 bg-v7-bronze/10 px-2 py-0.5 text-xs text-v7-bronze-dark" : "inline-flex rounded-full border border-rp-amber/20 bg-rp-amber/10 px-2 py-0.5 text-xs text-rp-amber"}>
                {sidebarManagedCount} managed
              </span>
            )}
          </div>
        ) : (
          <div className={appearance === "alpha"? `text-sm italic text-v7-ink/60` : `text-sm italic ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
            No strategy configured
          </div>
        )}
      </div>
    </div>
  );
};

export default UserInvestorStrategyCard;
